import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import { ITreeNodeSelectionEvent, IgxTreeComponent } from '@infragistics/igniteui-angular/tree';
import { Utilities } from "@tylertech/shared-module";
import { ITreeViewMetadata, PropertyMetadata } from "@tylertech/tyler-api-types";
import { RepositoryFormData } from "../../../interfaces";
import { MetadataActionService, MetadataEventService, MetadataPropertyService, MetadataService } from "../../../services";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ObjectHelpers } from "../../../utilities";

export interface ITreeNode
{
    Children: ITreeNode[];
    Disabled: boolean;
    Display: string;
    Icon: string;
    Id: string;
    Selected: boolean;
    Entity: any;
}

@Component({
    selector: "tree-view",
    templateUrl: "./treeView.tmpl.html",
    styleUrls: ["./treeView.component.scss"],
    standalone: false
})
export class TreeViewComponent implements OnInit
{
    private $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataActionService = inject(MetadataActionService);
    private $metadataEventService = inject(MetadataEventService);

    @ViewChild("treeView") treeView: IgxTreeComponent;

    @Input()
    viewMetadata: ITreeViewMetadata;

    @Input()
    entities: any[];

    @Input()
    datacontroller: RepositoryFormData;

    readonly CASCADING = "Cascading";
    readonly SINGLE_NODE = "None";
    readonly DEFAULT_SELECT_TYPE = this.SINGLE_NODE;

    nodes: ITreeNode[];
    selectType: string;
    property: PropertyMetadata;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (!args.Recalculating)
                    self.nodes.forEach(node => self.updateNodeEntities(node));
            });
    }

    ngOnInit(): void
    {
        const self = this;
        this.$metadataService.AugmentModels(this.viewMetadata.GridProperties.ClassMetadata, this.entities, this.viewMetadata.SelectProperty ? this.viewMetadata.SelectProperty : "Selected")
            .then(() =>
            {
                self.property = self.viewMetadata.GridProperties.ClassMetadata.Properties[0];
                self.createTree();
                self.selectType = Utilities.IsNullOrUndefined(self.viewMetadata.SelectType) ? self.DEFAULT_SELECT_TYPE : self.viewMetadata.SelectType;
            });
    }

    createTree(): void
    {
        this.nodes = [];
        this.entities.forEach((entity, index) =>
        {
            //Top-down tree
            if (this.viewMetadata.NodesProperty)
            {
                const topNode: ITreeNode = <ITreeNode>{
                    Id: this.getId(entity, index),
                    Display: entity[this.viewMetadata.DisplayProperty],
                    Selected: entity[this.viewMetadata.SelectProperty],
                    Disabled: this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"]),
                    Icon: entity[this.viewMetadata.IconProperty],
                    Entity: entity
                };
                if (!Utilities.IsNullOrUndefinedOrEmpty(entity[this.viewMetadata.NodesProperty]))
                    topNode.Children = this.createChildNodes(entity);
                this.nodes.push(topNode);
            }
            //Bottom-up tree
            else if (this.viewMetadata.Nodes)
            {
                // const newNode: ITreeNode = <ITreeNode>{
                //     Id: this.getId(entity, index),
                //     Display: entity[this.viewMetadata.DisplayProperty],
                //     Selected: entity[this.viewMetadata.SelectProperty],
                //     Disabled: this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"]),
                //     Icon: entity[this.viewMetadata.IconProperty]
                // };

                // let nodesList: string[] = [];
                // this.viewMetadata.Nodes.forEach(n => nodesList.push(entity[n]));
                // if (!nodesList)
                //     return;
                // let currentParent: ITreeNode;
                // nodesList.forEach((n, i) =>
                // {
                //     if (i === 0)
                //         currentParent = getOrCreateNode(n);
                //     else
                //         currentParent = getOrCreateNode(n, currentParent);
                //     if (i === nodesList.length - 1)
                //     {
                //         newNode.Parent = currentParent;
                //         currentParent.Children.push(newNode);
                //     }
                // });
            }
        });

        // function getOrCreateNode(display: string, parentNode?: ITreeNode): ITreeNode
        // {
        //     let children: ITreeNode[];
        //     if (!parentNode)
        //         children = self.nodes;
        //     else
        //         children = parentNode.Children;
        //     if (children.filter(x => x.Display === display).length > 0)
        //         return children.find(x => x.Display === display);
        //     const newNode = <ITreeNode>{
        //         Display: display,
        //         Children: []
        //     };
        //     newNode.Parent = parentNode;
        //     children.push(newNode);
        //     return newNode;
        // }
    }

    createChildNodes(parentEntity: any): ITreeNode[]
    {
        return parentEntity[this.viewMetadata.NodesProperty].map((entity, index) =>
        {
            const treeNode = <ITreeNode>{
                Id: this.getId(entity, index),
                Display: entity[this.viewMetadata.DisplayProperty],
                Selected: entity[this.viewMetadata.SelectProperty],
                Disabled: this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"]),
                Icon: entity[this.viewMetadata.IconProperty],
                Entity: entity
            };
            if (!Utilities.IsNullOrUndefinedOrEmpty(entity[this.viewMetadata.NodesProperty]))
                treeNode.Children = this.createChildNodes(entity);
            return treeNode;
        });
    }

    expandAll(expand): void
    {
        if (expand)
            this.treeView.expandAll();
        else
            this.treeView.collapseAll();
    }

    nodeSelection(event: ITreeNodeSelectionEvent): void
    {
        this.selectAll(false);
        this.selectNodes(this.nodes, true, event.newSelection.filter(x => !Utilities.IsNullOrUndefined(x) && !Utilities.IsNullOrUndefined(x.data.Id)).map(m => m.data.Id));
    }

    selectAll(select: boolean): void
    {
        this.treeView.nodes.forEach(x =>
        {
            if (!x.disabled)
                x.selected = select;
        });
        this.entities.forEach(e => this.setSelected(e, select));
        this.selectNodes(this.nodes, select);
    }

    private selectNodes(nodes: ITreeNode[], select: boolean, ids: string[] = null): void
    {
        nodes.filter(n => !n.Disabled).forEach(x =>
        {
            if (Utilities.IsNullOrUndefined(ids))
            {
                x.Selected = select;
            }
            else if (ids.indexOf(x.Id) >= 0)
            {
                x.Selected = select;
            }
            if (x.Children?.length > 0)
            {
                this.selectNodes(x.Children, select, ids);
            }
        });
        if (!Utilities.IsNullOrUndefined(ids))
        {
            this.entities.forEach((e, i) => this.setSelected(e, select, ids, i));
        }
    }

    setSelected(entity: any, select: boolean, ids: string[] = null, index: number = null): void
    {
        if ((ids === null || ids.indexOf(this.getId(entity, index)) > -1) && !this.$metadataPropertyService.isDisabled(this.property, entity, entity["$$Parent"]))
            entity[this.viewMetadata.SelectProperty] = select;
        if (entity[this.viewMetadata.NodesProperty])
            entity[this.viewMetadata.NodesProperty].forEach(e => this.setSelected(e, select, ids, index));
    }

    get selectTypeClass(): string
    {
        if (Utilities.IsNullOrUndefined(this.selectType))
        {
            return "select-single-node";
        }

        switch (this.selectType)
        {
            case this.CASCADING:
                return "select-cascading";
                break;
            case this.SINGLE_NODE:
                return "select-single-node";
                break;
            default:
                return "select-single-node";
        }
    }

    nodeClicked(node, event): void
    {
        if (this.selectType === this.SINGLE_NODE)
        {
            if (event.target.className.toString().indexOf("igx-icon") < 0)
            {
                this.selectAll(false);
                this.selectNodes(this.nodes, true, [node.Id]);
                if (!node.disabled && node.Id)
                    this.nodeClick(this.entities.find((e, i) => this.getId(e, i) === node.Id.toString()));
            }
        }
        event.stopPropagation();
    }

    nodeClick(model: any): void
    {
        const self = this;
        this.$metadataActionService
            .ExecuteAction(model, this.viewMetadata.NodeAction, this.datacontroller)
            .then(_ =>
            {
                self.nodes.forEach(node => self.updateNodeEntities(node));
            });
    }

    updateNodeEntities(node: ITreeNode): void
    {
        if (node.Entity)
        {
            node.Display = node.Entity[this.viewMetadata.DisplayProperty];
            node.Selected = node.Entity[this.viewMetadata.SelectProperty];
            node.Disabled = this.$metadataPropertyService.isDisabled(this.property, node.Entity, node.Entity["$$Parent"]);
            node.Icon = node.Entity[this.viewMetadata.IconProperty];
        }
        if (node.Children)
            node.Children.forEach(child => this.updateNodeEntities(child));
    }

    getId(entity: any, index: number): string
    {
        return ObjectHelpers.GetId(this.viewMetadata.GridProperties.IdProperty, entity, index).toString();
    }

    get title(): string
    {
        return this.viewMetadata.Title;
    }
}
